import { useEffect, useState, type FormEvent } from "react";
import { Navigate } from "react-router-dom";
import { signInWithEmailAndPassword } from "firebase/auth";
import {
  Box,
  Button,
  Center,
  FormControl,
  FormLabel,
  Heading,
  Icon,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  Spinner,
  Stack,
  Text,
} from "@chakra-ui/react";
import { Eye, EyeOff } from "lucide-react";
import { auth } from "../../utilities/firebase";
import { useAuth } from "../../services/auth/AuthProvider";

export const LoginPage = () => {
  const { status } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [shouldRedirect, setShouldRedirect] = useState(false);

  useEffect(() => {
    if (status === "admin") {
      const timer = setTimeout(() => {
        setShouldRedirect(true);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [status]);

  if (status === "loading") {
    return (
      <Center minH="100vh" px={4} bg="gray.50">
        <Box
          bg="white"
          p={{ base: 6, md: 10 }}
          borderRadius="2xl"
          boxShadow="md"
          w="full"
          maxW="400px"
          textAlign="center"
        >
          <Stack spacing={4} align="center">
            <Spinner size="xl" color="orange.500" thickness="3px" />
            <Text color="gray.700" fontSize="md" fontWeight="medium">
              Виконується вхід в кабінет видавця...
            </Text>
          </Stack>
        </Box>
      </Center>
    );
  }

  // Already an admin -> show message briefly, then redirect into the cabinet.
  if (status === "admin") {
    if (shouldRedirect) {
      return <Navigate to="/publishers/panel" replace />;
    }

    return (
      <Center minH="100vh" px={4} bg="gray.50">
        <Box
          bg="white"
          p={{ base: 6, md: 10 }}
          borderRadius="2xl"
          boxShadow="md"
          w="full"
          maxW="400px"
          textAlign="center"
        >
          <Stack spacing={4} align="center">
            <Spinner size="xl" color="orange.500" thickness="3px" />
            <Text color="gray.700" fontSize="md" fontWeight="medium">
              Виконується вхід в кабінет видавця...
            </Text>
          </Stack>
        </Box>
      </Center>
    );
  }

  if (status === "not-admin") return <Navigate to="/publishers/panel/no-access" replace />;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await signInWithEmailAndPassword(auth, email, password);
      // AuthProvider picks up the session and resolves the admin status,
      // so keep submitting active until unmount or status redirect.
    } catch {
      setError("Невірний email або пароль");
      setSubmitting(false);
    }
  };

  return (
    <Center minH="100vh" px={4} bg="gray.50">
      <Box
        as="form"
        onSubmit={onSubmit}
        bg="white"
        p={{ base: 6, md: 10 }}
        borderRadius="2xl"
        boxShadow="md"
        w="full"
        maxW="400px"
      >
        <Stack spacing={6}>
          <Box textAlign="center">
            <Heading size="lg" color="brand.charcoal">
              Кабінет видавця
            </Heading>
            <Text mt={2} color="gray.500" fontSize="sm">
              Увійдіть, щоб керувати книгами
            </Text>
          </Box>

          <FormControl isRequired>
            <FormLabel>Email</FormLabel>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
            />
          </FormControl>

          <FormControl isRequired>
            <FormLabel>Пароль</FormLabel>
            <InputGroup>
              <Input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
              />
              <InputRightElement>
                <IconButton
                  aria-label={showPassword ? "Сховати пароль" : "Показати пароль"}
                  icon={<Icon as={showPassword ? EyeOff : Eye} boxSize={4} />}
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowPassword((prev) => !prev)}
                />
              </InputRightElement>
            </InputGroup>
          </FormControl>

          {error && (
            <Text color="red.500" fontSize="sm">
              {error}
            </Text>
          )}

          <Button
            type="submit"
            colorScheme="orange"
            isLoading={submitting}
            loadingText="Вхід..."
            w="full"
          >
            Увійти
          </Button>
        </Stack>
      </Box>
    </Center>
  );
};
